// Formas de pagamento do orçamento (ORCA-1926 / ORCA-1931).
// Mesma conta da API (app/Services/Proposal/PaymentMethods/PaymentPlanCalculator.php):
// mantenha as duas iguais.
//
// - Base = total do orçamento (itens + avulsos + frete − desconto geral).
// - Desconto da forma só em 1x, sobre a base.
// - Entrada (PIX/dinheiro e boleto) sai antes de parcelar; em % é sobre a base.
// - Acima de "sem juros até": tabela Price com juros ao mês.
// - Parcela arredondada em centavos; a diferença vai para a última.

import { formatBRL } from './money';

export type PaymentMethodKey = 'credit_card' | 'debit_card' | 'pix_cash' | 'boleto';

/** Configuração de uma forma, no formato da API (snake_case) */
export interface PaymentMethodConfig {
    method: PaymentMethodKey;
    discount_percent: number;
    max_installments: number;
    interest_free_installments: number;
    interest_rate: number;
    entry_type: 'raw' | 'percent' | null;
    entry_amount: number;
}

export interface PaymentPlan {
    method: PaymentMethodKey;
    label: string;
    installments: number;
    base: number;
    discount_percent: number;
    discount_amount: number;
    entry_value: number;
    financed: number;
    installment_value: number;
    last_installment_value: number;
    interest_rate: number;
    interest_amount: number;
    total: number;
}

/** Forma descrita pela API (ProposalResource.payment_methods) */
export interface PaymentMethodDescribed extends PaymentMethodConfig {
    label: string;
    entry_value: number;
    options: PaymentPlan[];
}

/** Forma escolhida (ProposalResource.chosen_payment) */
export interface ChosenPayment extends PaymentPlan {
    description?: string;
    detail?: string;
    chosen_at?: string | null;
    source?: 'link' | 'system' | null;
}

export interface PaymentMethodMeta {
    key: PaymentMethodKey;
    label: string;
    icon: string;
    installable: boolean;
    withEntry: boolean;
}

/** Ordem fixa de exibição */
export const PAYMENT_METHODS: PaymentMethodMeta[] = [
    { key: 'credit_card', label: 'Cartão de crédito', icon: 'credit_card', installable: true, withEntry: false },
    { key: 'debit_card', label: 'Cartão de débito', icon: 'payment', installable: false, withEntry: false },
    { key: 'pix_cash', label: 'PIX ou dinheiro', icon: 'qr_code_2', installable: false, withEntry: true },
    { key: 'boleto', label: 'Boleto bancário', icon: 'receipt_long', installable: true, withEntry: true },
];

export const MAX_INSTALLMENTS = 24;

export function paymentMethodMeta(key: string | null | undefined): PaymentMethodMeta | undefined {
    return PAYMENT_METHODS.find((m) => m.key === key);
}

/** Arredonda em centavos como o PHP (meio para cima, longe do zero) */
export function roundMoney(value: number): number {
    const v = Number(value) || 0;
    const sign = v < 0 ? -1 : 1;
    return (sign * Math.round(Number((Math.abs(v) * 100).toFixed(8)))) / 100;
}

function num(value: any, fallback = 0): number {
    const n = typeof value === 'string' ? Number.parseFloat(value.replace(',', '.')) : Number(value);
    return Number.isFinite(n) ? n : fallback;
}

export function normalizePaymentMethod(config: Partial<PaymentMethodConfig> & { method: string }): PaymentMethodConfig {
    const meta = paymentMethodMeta(config.method);
    const installable = !!meta?.installable;
    const withEntry = !!meta?.withEntry;

    const max = installable ? Math.min(MAX_INSTALLMENTS, Math.max(1, Math.trunc(num(config.max_installments, 1)))) : 1;
    let free = installable ? Math.trunc(num(config.interest_free_installments, max)) : 1;
    free = Math.min(Math.max(1, free), max);
    let entryAmount = withEntry ? Math.max(0, num(config.entry_amount)) : 0;
    let entryType: 'raw' | 'percent' | null = withEntry ? ((config.entry_type as any) || null) : null;
    if (entryAmount <= 0) {
        entryAmount = 0;
        entryType = null;
    }

    return {
        method: config.method as PaymentMethodKey,
        discount_percent: Math.max(0, num(config.discount_percent)),
        max_installments: max,
        interest_free_installments: free,
        interest_rate: installable && max > free ? Math.max(0, num(config.interest_rate)) : 0,
        entry_type: entryType ?? (entryAmount > 0 ? 'raw' : null),
        entry_amount: entryAmount,
    };
}

export function entryValue(base: number, config: PaymentMethodConfig): number {
    const c = normalizePaymentMethod(config);
    if (c.entry_amount <= 0) {
        return 0;
    }
    return c.entry_type === 'percent' ? roundMoney((base * c.entry_amount) / 100) : roundMoney(c.entry_amount);
}

export function paymentPlan(baseValue: number, config: PaymentMethodConfig, installments: number): PaymentPlan {
    const c = normalizePaymentMethod(config);
    const base = roundMoney(Math.max(0, num(baseValue)));
    const n = Math.min(Math.max(1, Math.trunc(installments)), c.max_installments);

    const discountAmount = n === 1 ? roundMoney((base * c.discount_percent) / 100) : 0;
    const gross = roundMoney(base - discountAmount);
    const entry = Math.min(entryValue(base, c), gross);
    const financed = roundMoney(gross - entry);

    const hasInterest = n > c.interest_free_installments && c.interest_rate > 0;
    const rate = hasInterest ? c.interest_rate / 100 : 0;

    let installment: number;
    let financedTotal: number;
    if (rate > 0 && financed > 0) {
        const pmt = (financed * rate) / (1 - Math.pow(1 + rate, -n));
        installment = roundMoney(pmt);
        financedTotal = roundMoney(pmt * n);
    } else {
        installment = roundMoney(financed / n);
        financedTotal = financed;
    }

    return {
        method: c.method,
        label: paymentMethodMeta(c.method)?.label ?? c.method,
        installments: n,
        base,
        discount_percent: n === 1 ? c.discount_percent : 0,
        discount_amount: discountAmount,
        entry_value: entry,
        financed,
        installment_value: installment,
        last_installment_value: roundMoney(financedTotal - installment * (n - 1)),
        interest_rate: hasInterest ? c.interest_rate : 0,
        interest_amount: roundMoney(financedTotal - financed),
        total: roundMoney(entry + financedTotal),
    };
}

export function paymentOptions(base: number, config: PaymentMethodConfig): PaymentPlan[] {
    const c = normalizePaymentMethod(config);
    const options: PaymentPlan[] = [];
    for (let n = 1; n <= c.max_installments; n++) {
        options.push(paymentPlan(base, c, n));
    }
    return options;
}

export function formatPercent(value: number): string {
    return `${(Number(value) || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}%`;
}

/** Resumo das condições: "À vista ou até 5x (2,99% a.m.)", "Entrada R$ 200,00 + até 3x sem juros", "Entrada de R$ 200,00 + 1x de R$ 800,00", "À vista com 5% de desconto" */
export function paymentMethodSummary(base: number, config: PaymentMethodConfig): string {
    const c = normalizePaymentMethod(config);
    const meta = paymentMethodMeta(c.method);
    const entry = entryValue(base, c);
    const parts: string[] = [];

    if (meta?.installable && c.max_installments > 1) {
        if (c.interest_free_installments >= c.max_installments || c.interest_rate <= 0) {
            parts.push(`até ${c.max_installments}x sem juros`);
        } else if (c.interest_free_installments > 1) {
            parts.push(`até ${c.interest_free_installments}x sem juros ou até ${c.max_installments}x (${formatPercent(c.interest_rate)} a.m.)`);
        } else {
            parts.push(`${entry > 0 ? '1x' : 'à vista'} ou até ${c.max_installments}x (${formatPercent(c.interest_rate)} a.m.)`);
        }
    } else if (entry > 0) {
        // ORCA-1945 (revisão): com entrada e sem parcelamento, "Entrada de R$ x + 1x de R$ y"
        parts.push(`1x de ${formatBRL(paymentPlan(base, c, 1).financed)}`);
    } else {
        parts.push('à vista');
    }

    let text = (entry > 0 ? `entrada de ${formatBRL(entry)} + ` : '') + parts.join('');
    if (c.discount_percent > 0) {
        text += c.max_installments > 1
            ? ` · ${formatPercent(c.discount_percent)} de desconto à vista`
            : ` com ${formatPercent(c.discount_percent)} de desconto`;
    }
    return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Valor em destaque: menor preço à vista ou "até 5x de R$ 240,12" */
export function paymentMethodHeadline(base: number, config: PaymentMethodConfig): string {
    const c = normalizePaymentMethod(config);
    if (c.max_installments > 1) {
        const last = paymentPlan(base, c, c.max_installments);
        return `até ${c.max_installments}x de ${formatBRL(last.installment_value)}`;
    }
    return formatBRL(paymentPlan(base, c, 1).total);
}

/**
 * "Cartão de crédito - 4x de R$ 583,62", "PIX ou dinheiro - À vista",
 * "PIX ou dinheiro - Entrada de R$ 1.900,00 + 1x de R$ 1.900,00" (igual ao PaymentPlanCalculator::describe)
 */
export function describePlan(plan: PaymentPlan): string {
    const label = plan.label || paymentMethodMeta(plan.method)?.label || plan.method;
    const parts: string[] = [];
    if (plan.entry_value > 0) {
        parts.push(`Entrada de ${formatBRL(plan.entry_value)}`);
    }
    // ORCA-1973: com entrada, a parcela única mostra o valor ("+ 1x de R$ y") em vez de "+ à vista"
    if (plan.installments > 1 || plan.entry_value > 0) {
        parts.push(`${plan.installments}x de ${formatBRL(plan.installment_value)}`);
    } else {
        parts.push('À vista');
    }
    return `${label} - ${parts.join(' + ')}`;
}

/** "4x de R$ 583,62 (última R$ 583,63) · total R$ 2.334,49 (juros de 2,99% a.m.)" */
export function detailPlan(plan: PaymentPlan): string {
    let text = plan.installments > 1 ? `${plan.installments}x de ${formatBRL(plan.installment_value)}` : formatBRL(plan.financed);
    if (Math.abs(plan.last_installment_value - plan.installment_value) >= 0.005) {
        text += ` (última ${formatBRL(plan.last_installment_value)})`;
    }
    if (plan.entry_value > 0) {
        text = `Entrada ${formatBRL(plan.entry_value)} + ${text}`;
    } else if (plan.installments === 1) {
        text = 'À vista';
    }
    text += ` · total ${formatBRL(plan.total)}`;
    if (plan.interest_amount > 0) {
        text += ` (juros de ${formatPercent(plan.interest_rate)} a.m.)`;
    } else if (plan.discount_amount > 0) {
        text += ` (desconto de ${formatPercent(plan.discount_percent)})`;
    }
    return text;
}

/** Nota de uma opção de parcela: "sem juros", "total R$ 1.183,44", "· última R$ 240,13" */
export function optionNote(plan: PaymentPlan): string {
    let note = plan.interest_amount > 0 ? `total ${formatBRL(plan.total)}` : (plan.discount_amount > 0 ? `${formatPercent(plan.discount_percent)} de desconto` : 'sem juros');
    if (Math.abs(plan.last_installment_value - plan.installment_value) >= 0.005) {
        note += ` · última ${formatBRL(plan.last_installment_value)}`;
    }
    return note;
}
